import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Video, Sparkles, Loader2, CheckCircle2, ChevronDown, Settings2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline manager for a single client session (admin side):
 * - Set/clear the Zoom join link
 * - Edit manual coach notes
 * - Mark complete / reopen
 * - Paste a transcript -> AI generates polished recap + takeaways + action plan
 */
export default function AdminSessionManager({ session, onChange }) {
  const [open, setOpen] = useState(false);
  const [zoomUrl, setZoomUrl] = useState(session.zoom_url || '');
  const [notes, setNotes] = useState(session.notes || '');
  const [transcript, setTranscript] = useState(session.transcript || '');
  const [savingMeta, setSavingMeta] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [createTasks, setCreateTasks] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  const patch = async (payload, msg) => {
    setSavingMeta(true);
    try {
      const res = await axios.patch(`${API_URL}/api/admin/sessions/${session.session_id}`, payload, { withCredentials: true });
      toast.success(msg || 'Saved');
      onChange?.(res.data);
    } catch {
      toast.error('Save failed');
    } finally {
      setSavingMeta(false);
    }
  };

  const generateAI = async () => {
    if (transcript.trim().length < 40) { toast.error('Paste a transcript (at least 40 characters) first'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/sessions/${session.session_id}/ai-notes`, { transcript, create_tasks: createTasks, send_email: sendEmail }, { withCredentials: true });
      const bits = [];
      if (res.data.tasks_created) bits.push(`${res.data.tasks_created} task${res.data.tasks_created === 1 ? '' : 's'} added`);
      if (res.data.email_sent) bits.push('recap emailed');
      toast.success(`AI recap generated${bits.length ? ' · ' + bits.join(' · ') : ''}`);
      onChange?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const takeaways = session.ai_key_takeaways || [];
  const actionPlan = session.ai_action_plan || [];

  return (
    <div className="mt-3 border-t border-[#E2E8F0] pt-3">
      <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-sm font-medium text-[#1D4ED8]" data-testid={`manage-session-${session.session_id}`}>
        <Settings2 size={15} /> Manage session
        <ChevronDown size={15} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-5" data-testid={`session-manager-${session.session_id}`}>
          {/* Zoom link */}
          <div>
            <label className="block text-xs font-semibold text-[#475569] mb-1.5 flex items-center gap-1.5"><Video size={13} /> Zoom join link <span className="font-normal text-[#94A3B8]">(leave blank to use native room)</span></label>
            <div className="flex gap-2">
              <Input value={zoomUrl} onChange={(e) => setZoomUrl(e.target.value)} placeholder="https://zoom.us/j/..." data-testid={`zoom-url-${session.session_id}`} />
              <Button type="button" onClick={() => patch({ zoom_url: zoomUrl }, 'Zoom link saved')} disabled={savingMeta} className="btn-outline whitespace-nowrap">Save</Button>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#475569]">Status:</span>
            <span className={`text-xs px-2 py-0.5 rounded ${session.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-[#EFF6FF] text-[#1D4ED8]'}`}>{session.status}</span>
            {session.status === 'completed' ? (
              <Button type="button" onClick={() => patch({ status: 'scheduled' }, 'Reopened')} disabled={savingMeta} className="btn-outline text-xs h-7 px-3">Reopen</Button>
            ) : (
              <Button type="button" onClick={() => patch({ status: 'completed' }, 'Marked complete')} disabled={savingMeta} className="btn-outline text-xs h-7 px-3" data-testid={`mark-complete-${session.session_id}`}>Mark complete</Button>
            )}
          </div>

          {/* Manual notes */}
          <div>
            <label className="block text-xs font-semibold text-[#475569] mb-1.5">Coach notes (shared with client)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Agenda or manual notes the client will see..." rows={3} data-testid={`notes-${session.session_id}`} />
            <Button type="button" onClick={() => patch({ notes }, 'Notes saved')} disabled={savingMeta} className="btn-outline mt-2 text-xs h-8">Save notes</Button>
          </div>

          {/* AI transcript -> recap */}
          <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <label className="block text-xs font-semibold text-[#475569] mb-1.5 flex items-center gap-1.5"><Sparkles size={13} className="text-[#F97316]" /> Paste session transcript → AI recap, takeaways &amp; action plan</label>
            <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} placeholder="Paste the raw session transcript or rough notes here..." rows={4} data-testid={`transcript-${session.session_id}`} />
            <div className="flex flex-wrap items-center gap-4 mt-2">
              <label className="flex items-center gap-1.5 text-xs text-[#475569] cursor-pointer">
                <input type="checkbox" checked={createTasks} onChange={(e) => setCreateTasks(e.target.checked)} data-testid={`opt-create-tasks-${session.session_id}`} /> Add action items to client's tasks
              </label>
              <label className="flex items-center gap-1.5 text-xs text-[#475569] cursor-pointer">
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid={`opt-send-email-${session.session_id}`} /> Email recap to client
              </label>
            </div>
            <Button type="button" onClick={generateAI} disabled={generating} className="btn-primary-blue mt-2 text-sm" data-testid={`generate-ai-${session.session_id}`}>
              {generating ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Generating…</> : <><Sparkles size={14} className="mr-1.5" /> Generate AI recap</>}
            </Button>

            {(session.ai_summary || takeaways.length > 0) && (
              <div className="mt-4 pt-3 border-t border-[#E2E8F0] space-y-3" data-testid={`ai-result-${session.session_id}`}>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700"><CheckCircle2 size={13} /> Generated &amp; visible to the client</div>
                {session.ai_summary && <p className="text-sm text-[#475569] whitespace-pre-wrap">{session.ai_summary}</p>}
                {takeaways.length > 0 && (
                  <ul className="list-disc list-inside text-sm text-[#475569] space-y-1">
                    {takeaways.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                )}
                {actionPlan.length > 0 && (
                  <div className="text-sm text-[#475569]">
                    <div className="font-medium text-[#0A0F1C] mb-1">Action plan:</div>
                    <ol className="list-decimal list-inside space-y-1">
                      {actionPlan.map((a, i) => <li key={i}>{a.task || a}{a.why ? <span className="text-[#94A3B8]"> — {a.why}</span> : null}</li>)}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
